/* SpawnAnything — "Bauen": the build menu as its own window (user choice: every setup area gets a window like the
   Welt-Optionen). Every building is a card with a rotating voxel model, grouped by era, then "Weitere", the AI designs
   of this session and "Meine Sammlung" (src/game/collection.js). The window owns no building state: it drives the
   existing panel controls (#bKey, #bTeam0/1, [data-blvl], #bPlace, #bRot, #bClear) so placement, campaign and
   multiplayer keep working unchanged. main.js passes small functions:
     registry()            -> BREG (key -> { recipe, name, era, ai })
     eras                  -> [{ key, name }] (src/content/eras.js)
     info(key, level)      -> short stat text (size, HP, armour, what it does)
     generate(text, team)  -> Promise<{ key, name, recipe, source } | null>  (the "Armee beschreiben" building path)
     toast(text) */
import './buildWindow.css';
import { createWindow, card } from './window.js';
import { getPreview3D } from './preview3d.js';
import { buildingObject } from './models3d.js';
import { collection } from '../game/collection.js';

const $ = id => document.getElementById(id);
const COL = 'col_'; // BREG key prefix of saved buildings (main.js registers them)

export function createBuildWindow({ registry, eras = [], info = () => '', generate = null, toast = () => {} } = {}) {
  const pv = getPreview3D();
  const W = createWindow({ id: 'buildWin', title: 'Bauen', sub: 'Gebäude wählen und aufs Feld setzen', icon: 'ti-building-castle', wide: true,
    foot: matchMedia('(pointer: coarse)').matches ? 'Tippen aufs Feld setzt, Tippen auf ein Gebäude entfernt es' : 'Klick aufs Feld setzt, R dreht, Shift-Klick entfernt' });
  W.box.classList.add('bw');

  /* ---- top row: team, level, rotate, clear ---- */
  const top = document.createElement('div'); top.className = 'bw-top';
  top.innerHTML = `<div class="seg bw-team" role="group" aria-label="Team des Gebäudes"><button type="button" class="tb0" data-bwteam="0">Blau</button><button type="button" class="tb1" data-bwteam="1">Rot</button></div>
    <div class="seg bw-lvl" role="group" aria-label="Ausbaustufe"><button type="button" data-bwlvl="1">Stufe 1</button><button type="button" data-bwlvl="2">Stufe 2</button><button type="button" data-bwlvl="3">Stufe 3</button></div>
    <div class="bw-acts"><button type="button" class="ghost bw-rot" title="Dreht um 90° (Taste R)"><i class="ti ti-rotate-clockwise" aria-hidden="true"></i> Drehen</button><button type="button" class="ghost bw-clear"><i class="ti ti-trash" aria-hidden="true"></i> Alle entfernen<span class="bw-cnt"></span></button></div>`;
  W.body.appendChild(top);

  /* ---- AI row: describe a building, let the AI build it, keep it ---- */
  const ai = document.createElement('div'); ai.className = 'bw-ai';
  ai.innerHTML = `<div class="bw-ai-row"><input class="bw-prompt" maxlength="160" autocomplete="off" placeholder="z. B. Eisburg mit blauen Fahnen" aria-label="Gebäude beschreiben">
    <button type="button" class="ghost bw-gen"><i class="ti ti-sparkles" aria-hidden="true"></i> Gebäude von der KI bauen lassen</button></div>
    <div class="bw-res" hidden><span class="bw-res-t"></span><button type="button" class="ghost bw-res-place">Platzieren</button><button type="button" class="ghost bw-res-save"><i class="ti ti-bookmark" aria-hidden="true"></i> In Sammlung speichern</button></div>`;
  W.body.appendChild(ai);
  const inp = ai.querySelector('.bw-prompt'), genBtn = ai.querySelector('.bw-gen'), res = ai.querySelector('.bw-res');
  if (!generate) ai.hidden = true;

  const secs = document.createElement('div'); secs.className = 'bw-secs'; W.body.appendChild(secs);

  /* ---- current choice = the panel controls ---- */
  const curTeam = () => ($('bTeam1')?.getAttribute('aria-pressed') === 'true' ? 1 : 0);
  const curLevel = () => +(document.querySelector('#panel [data-blvl][aria-pressed=true]')?.dataset.blvl || 1);
  const curKey = () => $('bKey')?.value || '';
  let team = curTeam(), level = curLevel();

  function setTeam(t) {
    if (t !== curTeam()) $('bTeam' + t)?.click();
    team = t; syncTop(); restyle();
  }
  function setLevel(L) {
    if (L !== curLevel()) document.querySelector(`#panel [data-blvl="${L}"]`)?.click();
    level = L; syncTop(); restyle();
  }
  function syncTop() {
    top.querySelectorAll('[data-bwteam]').forEach(b => b.setAttribute('aria-pressed', String(+b.dataset.bwteam === team)));
    top.querySelectorAll('[data-bwlvl]').forEach(b => b.setAttribute('aria-pressed', String(+b.dataset.bwlvl === level)));
    const n = parseInt(($('bCount')?.textContent || '').replace(/\D/g, ''), 10) || 0;
    top.querySelector('.bw-cnt').textContent = n ? ` (${n})` : '';
    top.querySelector('.bw-clear').disabled = !n;
  }
  top.querySelectorAll('[data-bwteam]').forEach(b => b.addEventListener('click', () => setTeam(+b.dataset.bwteam)));
  top.querySelectorAll('[data-bwlvl]').forEach(b => b.addEventListener('click', () => setLevel(+b.dataset.bwlvl)));
  top.querySelector('.bw-rot').addEventListener('click', () => { $('bRot')?.click(); toast('Gebäude um 90° gedreht'); });
  top.querySelector('.bw-clear').addEventListener('click', () => { $('bClear')?.click(); syncTop(); toast('Alle Gebäude entfernt'); });

  /** select a building: drive the panel controls, close, arm placement (same as "Platzieren") */
  function select(key) {
    const sel = $('bKey'); if (!sel || !registry()[key]) return;
    if (team !== curTeam()) $('bTeam' + team)?.click();
    if (level !== curLevel()) document.querySelector(`#panel [data-blvl="${level}"]`)?.click();
    sel.value = key; sel.dispatchEvent(new Event('change', { bubbles: true }));
    W.close();
    const pb = $('bPlace'); if (pb && pb.getAttribute('aria-pressed') !== 'true') pb.click();
  }

  /* ---- cards ---- */
  const views = []; // { view, recipe, key, card }
  let sig = '';
  function groups() {
    const R = registry(), keys = Object.keys(R), known = new Set(eras.map(e => e.key));
    const out = eras.map(e => ({ title: e.name, keys: keys.filter(k => !R[k].ai && R[k].era === e.key) }));
    out.push({ title: 'Weitere', keys: keys.filter(k => !R[k].ai && !known.has(R[k].era)) });
    // AI designs of this session that are not saved yet (saved ones show under "Meine Sammlung")
    out.push({ title: 'KI-Entwürfe', keys: keys.filter(k => R[k].ai && !k.startsWith(COL) && !collection.has('building', R[k].recipe)), tag: 'KI' });
    out.push({ title: 'Meine Sammlung', keys: collection.list('building').map(e => COL + e.id).filter(k => R[k]), col: true, always: true });
    return out;
  }
  function render(force = false) {
    const G = groups(), s = G.map(g => g.title + ':' + g.keys.join(',')).join('|');
    if (!force && s === sig) { restyle(false); return; }
    sig = s;
    pv.removeWithin(secs); secs.textContent = ''; views.length = 0;
    const R = registry(), cur = curKey();
    for (const g of G) {
      if (!g.keys.length && !g.always) continue;
      const sec = document.createElement('section'); sec.className = 'aw-win-sec' + (g.col ? ' bw-col' : '');
      sec.innerHTML = `<h3>${g.col ? '<i class="ti ti-bookmarks" aria-hidden="true"></i>' : ''}<span></span></h3>`;
      sec.querySelector('h3 span').textContent = g.title;
      const grid = document.createElement('div'); grid.className = 'aw-grid'; sec.appendChild(grid);
      if (!g.keys.length) {
        const p = document.createElement('p'); p.className = 'bw-empty';
        p.textContent = 'Noch leer: lass die KI oben ein Gebäude bauen und speichere es hier.';
        sec.appendChild(p);
      }
      for (const k of g.keys) {
        const B = R[k], txt = info(k, level);
        const c = card({ title: B.name, text: txt, tag: g.tag || '', on: k === cur });
        c.title = B.name + (txt ? ': ' + txt : ''); c.dataset.key = k;
        c.addEventListener('click', () => select(k));
        const view = c.querySelector('.aw-card-view');
        pv.add(view, () => buildingObject(B.recipe, team, level), { clip: W.body, spin: 0.5 });
        views.push({ view, recipe: B.recipe, key: k, card: c });
        if (g.col) {
          const cell = document.createElement('div'); cell.className = 'bw-cell';
          cell.append(c, removeBtn(k.slice(COL.length), B.name));
          grid.appendChild(cell);
        } else grid.appendChild(c);
      }
      secs.appendChild(sec);
    }
    shownTeam = team; shownLevel = level; // the fresh cards already show this team / level
    restyle(false);
  }
  // ✕ on a saved building: first click asks, second click deletes (a saved AI design may have cost diamonds)
  function removeBtn(id, name) {
    const x = document.createElement('button');
    x.type = 'button'; x.className = 'bw-del'; x.textContent = '✕'; x.title = `${name} aus der Sammlung löschen`;
    x.setAttribute('aria-label', x.title);
    let armed = 0;
    x.addEventListener('click', e => {
      e.stopPropagation();
      if (!armed) { x.classList.add('ask'); x.textContent = 'Löschen?'; armed = setTimeout(() => { armed = 0; x.classList.remove('ask'); x.textContent = '✕'; }, 3000); return; }
      clearTimeout(armed); armed = 0;
      if (collection.remove(id)) toast(`${name} aus der Sammlung gelöscht`);
    });
    return x;
  }
  let shownTeam = team, shownLevel = level;
  /** team / level changed: rebuild the models, refresh texts and the selection mark */
  function restyle(models = true) {
    const cur = curKey(), rebuild = models || shownTeam !== team || shownLevel !== level;
    for (const v of views) {
      v.card.setAttribute('aria-pressed', String(v.key === cur));
      if (!rebuild) continue;
      const txt = info(v.key, level), s = v.card.querySelector('.aw-card-s');
      if (s) s.textContent = txt;
      v.card.title = registry()[v.key]?.name + (txt ? ': ' + txt : '');
      const rec = v.recipe;
      pv.rebuild(v.view, () => buildingObject(rec, team, level));
    }
    shownTeam = team; shownLevel = level;
  }

  /* ---- AI generation (pricing, validation and registry live in main.js) ---- */
  let busy = false, last = null;
  async function runGen() {
    const text = inp.value.trim();
    if (!text) { toast('Beschreibe zuerst das Gebäude, z. B. „Eisburg mit blauen Fahnen“.'); inp.focus(); return; }
    if (busy || !generate) return;
    busy = true; genBtn.disabled = inp.disabled = true; genBtn.classList.add('busy');
    try {
      const r = await generate(text, team);
      if (r && r.key && registry()[r.key]) { last = { ...r, prompt: text }; showResult(); render(); }
    } catch (e) { console.warn('Gebäude-KI fehlgeschlagen', e); toast('Gebäude konnte nicht erzeugt werden.'); }
    finally { busy = false; genBtn.disabled = inp.disabled = false; genBtn.classList.remove('busy'); }
  }
  function showResult() {
    if (!last) { res.hidden = true; return; }
    const placeholder = last.source !== 'model' && last.source !== 'library';
    const saved = collection.has('building', last.recipe);
    res.hidden = false;
    res.querySelector('.bw-res-t').textContent = placeholder ? `KI nicht erreichbar: „${last.name}“ ist ein Platzhalter` : `„${last.name}“ ist fertig.`;
    const sv = res.querySelector('.bw-res-save');
    sv.hidden = placeholder; sv.disabled = saved;
    sv.innerHTML = saved ? '<i class="ti ti-bookmark-filled" aria-hidden="true"></i> In der Sammlung' : '<i class="ti ti-bookmark" aria-hidden="true"></i> In Sammlung speichern';
  }
  genBtn.addEventListener('click', runGen);
  inp.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); runGen(); } });
  res.querySelector('.bw-res-place').addEventListener('click', () => { if (last) select(registry()[last.key] ? last.key : curKey()); });
  res.querySelector('.bw-res-save').addEventListener('click', () => {
    if (!last) return;
    const e = collection.add({ kind: 'building', name: last.name, prompt: last.prompt, recipe: last.recipe });
    toast(e ? `${last.name} ist jetzt in deiner Sammlung` : 'Speichern fehlgeschlagen (Speicher voll?)');
    showResult();
  });

  // saved buildings changed (here or elsewhere): main.js updates BREG first (it subscribed earlier), then we re-render
  collection.subscribe(() => { if (W.isOpen) { render(); showResult(); } else sig = ''; });

  W.onOpen(() => { team = curTeam(); level = curLevel(); syncTop(); render(); showResult(); });

  // entry point: the "Bauen" tile of the setup hub (src/ui/setupHub.js, main.js)

  function open() {
    if ($('panel')?.hidden) { toast('Gebaut wird vor der Schlacht.'); return; }
    W.open();
  }
  const close = () => W.close();
  return { open, close, toggle: () => (W.isOpen ? close() : open()), win: W };
}
